/**
 * @ComponentFor MagazineStartPageViewModel
 */

import connect from 'Shared/connect';
import React, { useState, useRef } from 'react';
import { styled } from '@glitz/react';
import { get, replaceState, scrollTo, URLX } from '@polarnopyret/scope';
import { EpiProperty, epiPropertyValue } from '@polarnopyret/scope-episerver';
import { currentUrl } from '@polarnopyret/scope/index';
import MagazineStartPageViewModel from './MagazineStartPageViewModel.type';
import { ResponsiveSize, ArticleTag, TextBlockSmall, H2 } from 'Shared/SharedComponents/atoms/Typography';
import Link from 'Shared/Link';
import { AspectRatioInner, AspectRatioOuter, OverlayNotABackdrop } from 'Shared/SharedComponents/atoms/AspectRatio';
import ArticleListViewModel from 'MagazineStart/ArticleListViewModel.type';
import { isCompact as functionIsCompact } from 'Shared/Viewport';
import Pagination from 'Shared/SharedComponents/atoms/Pagination';
import { calculateTopPadding, Ratio } from 'Shared/AspectRatio';
import { setImageBackground, Preset } from 'Shared/image-background';
import { TopContentWide } from 'Shared/Page/PageContent';
import { SeoContent } from 'Shared/Page/PageBottom';
import Page from 'Shared/Page';
import { GridWrap } from 'Shared/PageLayout';
import SwiperList from 'Shared/SwiperList';
import * as style from 'Shared/Style';
import GridOrListSelector from './GridOrListSelector';
import ListLayout from './ListLayout';
import FilterTags from './FilterTags';
import GridLayout from './GridLayout';
import { getCurrentBaseUrl, selectDeviceImage } from 'Shared/utils';
import { updateCanonicalUrl } from 'Shared/reducers';

type ConnectedPropType = {
  currentBreakpoint: number;
  headerHeight: number;
};

type PropType = MagazineStartPageViewModel & ConnectedPropType;

const ArticleOptions = styled.div({
  display: 'flex',
  flexDirection: 'column',
  marginBottom: '28px',
  ['@media ' + style.mediaMinLarge]: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: '40px',
  },
})

const ArticleOptionsFilters = styled.div({
  ['@media ' + style.mediaUpToLarge]: {
    marginRight: '-12px',
  },

  ['@media ' + style.mediaMinLarge]: {
    gap: '12px',
    display: 'flex',
    alignItems: 'center',
  },
})

const ArticleOptionsDisplay = styled.div({
  display: 'flex',
  gap: '12px',
  alignItems: 'center',
  ['@media ' + style.mediaUpToLarge]: {
    justifyContent: 'space-between',
  },
})

const TopContentInner = styled.div({
  ['@media ' + style.mediaMinLarge]: {
    marginRight: '16px',
  },
})

const DesktopHeadlineWrap = styled(AspectRatioOuter, {
  paddingTop: `${calculateTopPadding(Ratio.EightToThree)}`,
})

const DesktopHeadlineInner = styled(AspectRatioInner, {
  display: 'flex',
  gap: '16px',
})

const DesktopHeadlineLink = styled(Link, {
  textDecoration: 'none',
  color: 'white',
  height: '100%',
  transition: {
    property: 'flex-basis',
    duration: '0.3s',
    timingFunction: 'ease-in-out',
  },
})

const DesktopHeadlineBackground = styled.div({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  borderRadius: '8px',
  padding: { xy: '16px' },
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
})

const DesktopHeadlineContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
  position: 'relative',
})

const DesktopArticleTag = styled(ArticleTag, {
  transition: {
    property: 'opacity',
    duration: '0.3s',
    timingFunction: 'ease-in-out',
  },
})

const DesktopHeadlineHeading = styled(H2, {
  transition: {
    property: 'opacity',
    duration: '0.3s',
    timingFunction: 'ease-in-out',
  },
})

const DesktopHeadlineOverlay = styled(OverlayNotABackdrop, {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
  gap: '16px',
  transition: {
    property: 'opacity',
    duration: '0.3s',
    timingFunction: 'ease-in-out',
  },
})

const MobileHeadlineLink = styled(Link, {
  textDecoration: 'none', color: 'white'
})

const MobileHeadlineWrap = styled(AspectRatioOuter, {
  paddingTop: `${calculateTopPadding(Ratio.ThreeToFour)}`,
  backgroundSize: 'cover',
  backgroundPosition: 'center center',
  borderRadius: '8px',
})

const MobileHeadlineInner = styled(AspectRatioInner, {
  padding: { xy: '16px' },
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  alignItems: 'flex-start',
  gap: '16px',
  boxSizing: 'border-box',
})

const DesktopMobileContent = styled.div({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
})

export const MagazineStart = connect((state) => ({
  currentBreakpoint: state.currentBreakpoint,
  headerHeight: state.UI.headerHeight,
}),
  (dispatch) => ({
    updateCanonicalUrl(canonicalUrl: string) {
      return dispatch(updateCanonicalUrl(canonicalUrl));
    },
  }))(
    (props: PropType & {
      updateCanonicalUrl: (
        canonicalUrl: string
      ) => void;
      currentBreakpoint: number;
    }) => {
      const { currentBreakpoint, page, updateCanonicalUrl } = props;
      const [articleListViewModel, setArticleListViewModel] = useState<ArticleListViewModel | undefined>(
        props.articleListViewModel,
      );
      const isCompact = functionIsCompact(currentBreakpoint);
      const [gridLayout, setGridLayout] = useState(true);
      const [isHovered, setIsHovered] = useState(0);
      const params = currentUrl()?.searchParams;
      const selectedTag = params?.get('tag');
      let topRef = useRef<HTMLDivElement>();

      const onListNavigate = (number?: number, tag?: string) => {
        const url = currentUrl();

        if (number) {
          url.searchParams.set('page', number.toString());

          const canonicalUrl = getCurrentBaseUrl();
          if (number > 1) {
            canonicalUrl.searchParams.set('page', number.toString());
          }
          updateCanonicalUrl(canonicalUrl.toString());
        } else if (tag) {
          url.searchParams.delete('page');
          url.searchParams.set('tag', tag);
        }

        const displayUrl = url.toString();
        url.pathname = url.pathname + '/filter';
        get(url, {})
          .then((r) => r.json())
          .then((json: ArticleListViewModel) => {
            setArticleListViewModel(json);
            replaceState(displayUrl, null, false);
          })
          .then(() => {
            if (topRef) {
              scrollTo(topRef.current.offsetTop - 150, 500);
            }
          });
      };

      return (
        <Page>
          <TopContentWide
            isCompact={isCompact}
            label={epiPropertyValue(page.label)}
            heading={epiPropertyValue(page.heading)}
            intro={epiPropertyValue(page.mainIntro)}
            breadcrumbs={props.breadcrumbs}
          >
            <TopContentInner>
              {!isCompact && props.headlineArticles && props.headlineArticles.length > 0 && (
                <DesktopHeadlineWrap>
                  <DesktopHeadlineInner>
                    {props.headlineArticles.map((article, index) => {
                      return (
                        <DesktopHeadlineLink
                          key={index}
                          to={article.url}
                          css={{
                            flexBasis: isHovered === index ? '66%' : '33%',
                          }}
                          draggable={false}
                          onMouseEnter={() => setIsHovered(index)}
                          onMouseLeave={() => setIsHovered(0)}
                        >
                          <DesktopHeadlineBackground css={{ backgroundImage: setImageBackground(selectDeviceImage(article.image, article.imageSmallDevice, isCompact)?.url, Preset.MagazineTopItem), }}>
                            <DesktopHeadlineContent>
                              <DesktopArticleTag css={{ opacity: isHovered === index ? 0 : 1, }}>
                                {article.tag}
                              </DesktopArticleTag>
                              <DesktopHeadlineHeading
                                fontSize={ResponsiveSize.D32_M32}
                                upperCase={false}
                                css={{ opacity: isHovered === index ? 0 : 1, }}
                              >
                                {article.heading}
                              </DesktopHeadlineHeading>
                              <DesktopHeadlineOverlay css={{ opacity: isHovered === index ? 1 : 0, }}>
                                <ArticleTag>{article.tag}</ArticleTag>
                                <H2 fontSize={ResponsiveSize.D56_M32} upperCase={false}>
                                  {article.heading}
                                </H2>
                              </DesktopHeadlineOverlay>
                            </DesktopHeadlineContent>
                            <DesktopArticleTag
                              css={{
                                opacity: isHovered === index ? 1 : 0,
                              }}
                            >
                              {article.tags?.slice(0, 3).join(', ')}
                              {(article?.tags?.length ?? 0) > 3 && `, +${article?.tags?.length - 3}`}
                            </DesktopArticleTag>
                          </DesktopHeadlineBackground>
                        </DesktopHeadlineLink>
                      );
                    })}
                  </DesktopHeadlineInner>
                </DesktopHeadlineWrap>
              )}
              {isCompact && props.headlineArticles && props.headlineArticles.length > 0 && (
                <SwiperList
                  slidesPerView={isCompact ? 1.5 : 3.5}
                  spaceBetween={isCompact ? 12 : 16}
                  isCompact={isCompact}
                >
                  {props.headlineArticles.map((article, index) => (
                    <div key={index} className="swiper-slide">
                      <MobileHeadlineLink to={article.url} draggable={false}>
                        <MobileHeadlineWrap css={{ backgroundImage: setImageBackground(selectDeviceImage(article.image, article.imageSmallDevice, isCompact)?.url, Preset.MagazineTopItem), }}>
                          <MobileHeadlineInner>
                            <DesktopMobileContent>
                              <ArticleTag>{article.tag}</ArticleTag>
                              <H2 upperCase={false} fontSize={ResponsiveSize.D32_M32}>
                                {article.heading}
                              </H2>
                            </DesktopMobileContent>
                            <ArticleTag>
                              {article.tags?.slice(0, 3).join(', ')}
                              {(article?.tags?.length ?? 0) > 3 && `, +${(article?.tags?.length as number) - 3}`}
                            </ArticleTag>
                          </MobileHeadlineInner>
                        </MobileHeadlineWrap>
                      </MobileHeadlineLink>
                    </div>
                  ))}
                </SwiperList>
              )}
            </TopContentInner>
          </TopContentWide>
          <GridWrap>
            <ArticleOptions ref={topRef}>
              <ArticleOptionsFilters>
                <FilterTags
                  selectedTag={selectedTag}
                  articleListViewModel={props.articleListViewModel}
                  isCompact={isCompact}
                  onFilterClick={onListNavigate}
                />
                {!isCompact && <TextBlockSmall>{articleListViewModel.labels.nrItems}</TextBlockSmall>}
              </ArticleOptionsFilters>
              <ArticleOptionsDisplay>
                {isCompact && <TextBlockSmall css={{ flexGrow: 1 }}>{articleListViewModel.labels.nrItems}</TextBlockSmall>}
                <GridOrListSelector gridLayout={gridLayout} setGridLayout={setGridLayout} isCompact={isCompact} />
              </ArticleOptionsDisplay>
            </ArticleOptions>
          </GridWrap>
          {
            gridLayout ? (
              <GridLayout articleListViewModel={articleListViewModel} isCompact={isCompact} />
            ) : (
              <GridWrap><ListLayout articleListViewModel={articleListViewModel} isCompact={isCompact} /></GridWrap>
            )
          }
          <GridWrap>
            <Pagination
              currentPage={articleListViewModel.currentPageIndex}
              pageCount={articleListViewModel.totalPages}
              currentlyDisplayedItemCount={articleListViewModel.articles.length}
              totalItemCount={articleListViewModel.totalItemCount}
              totalItemCountLabel={articleListViewModel.labels.displayingItems}
              totalItemCountTextColor={style.colors.monochrome.black}
              navigateToPage={onListNavigate}
            />
          </GridWrap>
          <EpiProperty for={page.bottomContentArea} />
          <SeoContent
            heading={epiPropertyValue(props.page.bottomHeading)}
            text={epiPropertyValue(props.page.bottomText)}
          />
        </Page >
      );
    });

export default MagazineStart;
